import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { useAccount, useMsal } from '@azure/msal-react'
import axios from 'axios'
import React, { useEffect, useRef, type ReactElement } from 'react'
import { useIntl } from 'react-intl'
import { loginRequest } from '../../authConfig'
import localizedMessages from './messages'
import logger from '../../app/middleware/log'

interface RequestInterceptorProps {
  children?: React.ReactNode
}

const RequestInterceptor = (props: RequestInterceptorProps): ReactElement<any, any> => {
  const { instance, accounts } = useMsal()
  const account = useAccount(accounts[0])
  const { children } = props

  const { formatMessage } = useIntl()
  const formattedError = formatMessage(localizedMessages.noActiveAccount)

  const interceptorIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (interceptorIdRef.current !== null) {
      axios.interceptors.request.eject(interceptorIdRef.current)
    }

    /* eslint-disable no-param-reassign */
    interceptorIdRef.current = axios.interceptors.request.use(async (config) => {
      if (account == null) {
        throw Error(formattedError)
      }

      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        })
        config.headers.Authorization = `Bearer ${response.accessToken}`
        return config
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(loginRequest)
          return Promise.reject(error)
        }
        logger.log(error)
        return Promise.reject(error)
      }
    })
    /* eslint-enable no-param-reassign */

    return () => {
      if (interceptorIdRef.current !== null) {
        axios.interceptors.request.eject(interceptorIdRef.current)
      }
    }
  }, [account, instance, formattedError])

  return <>{children}</>
}

export default RequestInterceptor
